import { makeAutoObservable, runInAction } from "mobx";
import {
  superadminListClients,
  superadminGetClientsSummary,
  superadminListServiceCenters,
} from "@pro-sto/api-client";
import type {
  SuperadminClientListItemDto,
  PaginationMetaDto,
} from "@pro-sto/api-client";

export interface ClientSummaryDto {
  totalUsers: number;
  totalUsersChange: number;
  totalBookings: number;
  totalBookingsChange: number;
  appBookingsPct: number;
  appBookingsPctChange: number;
  activeUsersPct: number;
  activeUsersPctChange: number;
  charts: {
    totalBookings: Array<number>;
    labels: Array<string>;
  };
}
import { toastStore } from "./ToastStore";

export type SortDirection = "ascending" | "descending";

export interface SortDescriptor {
  column: string;
  direction: SortDirection;
}

export interface RangeFilter {
  from?: number;
  to?: number;
}

export interface ClientsFilters {
  serviceCenterIds: string[];
  bookings: RangeFilter;
  spent: RangeFilter;
}

const emptyFilters = (): ClientsFilters => ({
  serviceCenterIds: [],
  bookings: {},
  spent: {},
});

/** Cache key = "YYYY-MM" */
function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Typed response says {data, meta}, but real API returns {items, total, page, limit}.
 * This helper handles both shapes at runtime.
 */
function parseListResponse(
  raw: unknown,
  fallbackLimit: number,
): { items: SuperadminClientListItemDto[]; meta: PaginationMetaDto } {
  const r = raw as Record<string, unknown>;
  const items =
    (r["items"] as SuperadminClientListItemDto[] | undefined) ??
    (r["data"] as SuperadminClientListItemDto[] | undefined) ??
    [];
  const nestedMeta = r["meta"] as Partial<PaginationMetaDto> | undefined;
  const total = nestedMeta?.total ?? (r["total"] as number | undefined) ?? 0;
  const page = nestedMeta?.page ?? (r["page"] as number | undefined) ?? 1;
  const limit =
    nestedMeta?.limit ?? (r["limit"] as number | undefined) ?? fallbackLimit;
  const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
  const meta: PaginationMetaDto = {
    total,
    page,
    limit,
    totalPages,
    hasNextPage: nestedMeta?.hasNextPage ?? page < totalPages,
    hasPrevPage: nestedMeta?.hasPrevPage ?? page > 1,
  };
  return { items, meta };
}

interface MonthCache {
  clients: SuperadminClientListItemDto[];
  meta: PaginationMetaDto | null;
  summary: ClientSummaryDto | null;
}

class ClientsStore {
  // ── Per-month cache ──
  private monthDataCache: Map<string, MonthCache> = new Map();

  // List data
  clients: SuperadminClientListItemDto[] = [];
  meta: PaginationMetaDto | null = null;
  isLoading = false;
  isLoadingMore = false;

  // Summary data
  summary: ClientSummaryDto | null = null;
  isSummaryLoading = false;

  // Service centers for filter dropdown
  serviceCenterOptions: Array<{ uuid: string; name: string }> = [];
  isServiceCentersLoading = false;

  // Filters & search
  search = "";
  filters: ClientsFilters = emptyFilters();
  sortDescriptor: SortDescriptor = {
    column: "",
    direction: "descending",
  };

  // Month navigation
  activeMonth: Date = (() => {
    const d = new Date();
    d.setDate(1);
    return d;
  })();

  // Pagination
  page = 1;
  readonly limit = 20;

  constructor() {
    makeAutoObservable(this, {
      // @ts-ignore — Map is not observable by default
      monthDataCache: false,
    });
  }

  // ── Computed ──

  get hasNextPage(): boolean {
    return this.meta?.hasNextPage ?? false;
  }

  /**
   * Clients filtered client-side by serviceCenterIds.
   * Note: serviceCenterIds is NOT sent to the list API (backend doesn't support it).
   */
  get visibleClients(): SuperadminClientListItemDto[] {
    const ids = this.filters.serviceCenterIds;
    if (ids.length === 0) return this.clients;
    const idSet = new Set(ids);
    return this.clients.filter((c) => {
      const centers =
        (c as unknown as { serviceCenters?: Array<{ uuid: string }> })
          .serviceCenters ?? [];
      return centers.some((sc) => idSet.has(sc.uuid));
    });
  }

  get hasActiveFilters(): boolean {
    const f = this.filters;
    return !!(
      f.serviceCenterIds.length > 0 ||
      f.bookings.from !== undefined ||
      f.bookings.to !== undefined ||
      f.spent.from !== undefined ||
      f.spent.to !== undefined
    );
  }

  get activeFilterCount(): number {
    const f = this.filters;
    let count = 0;
    if (f.serviceCenterIds.length > 0) count++;
    if (f.bookings.from !== undefined || f.bookings.to !== undefined) count++;
    if (f.spent.from !== undefined || f.spent.to !== undefined) count++;
    return count;
  }

  // ── API param builders ──

  private apiListParams(page: number, month?: Date) {
    const f = this.filters;
    const d = month ?? this.activeMonth;
    const { column, direction } = this.sortDescriptor;
    const sort = column
      ? direction === "descending"
        ? `-${column}`
        : column
      : undefined;
    return {
      q: this.search || undefined,
      page,
      limit: this.limit,
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      sort,
      // serviceCenterIds intentionally excluded — backend returns error for this field;
      // client-side filtering is applied via visibleClients computed
      filterBookingsCountGte: f.bookings.from,
      filterBookingsCountLte: f.bookings.to,
      filterTotalSpentGte: f.spent.from,
      filterTotalSpentLte: f.spent.to,
    };
  }

  private apiSummaryParams(month?: Date) {
    const d = month ?? this.activeMonth;
    return {
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      q: this.search || undefined,
      // serviceCenterIds excluded — backend does not support this filter at runtime
    };
  }

  // ── Actions ──

  loadClients = async () => {
    this.isLoading = true;
    try {
      const response = await superadminListClients(this.apiListParams(1));
      const { items, meta } = parseListResponse(response, this.limit);
      runInAction(() => {
        this.clients = items;
        this.meta = meta;
        this.page = 1;
        this.isLoading = false;
      });
      this.saveToCache();
    } catch {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить список клиентов");
    }
  };

  loadMore = async () => {
    if (this.isLoadingMore || !this.hasNextPage) return;
    this.isLoadingMore = true;
    const nextPage = this.page + 1;
    try {
      const response = await superadminListClients(
        this.apiListParams(nextPage),
      );
      const { items: newItems, meta } = parseListResponse(response, this.limit);
      runInAction(() => {
        const existing = new Set(this.clients.map((c) => c.uuid));
        const unique = newItems.filter((c) => !existing.has(c.uuid));
        this.clients = [...this.clients, ...unique];
        this.meta = meta;
        this.page = nextPage;
        this.isLoadingMore = false;
      });
      this.saveToCache();
    } catch {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  };

  loadServiceCenters = async () => {
    if (this.serviceCenterOptions.length > 0) return; // already loaded
    this.isServiceCentersLoading = true;
    try {
      const response = await superadminListServiceCenters({
        limit: 100,
        page: 1,
      });
      runInAction(() => {
        const raw = response as unknown as {
          data?: Array<{ uuid: string; name: string }>;
        };
        this.serviceCenterOptions = (raw.data ?? [])
          .map((sc) => ({ uuid: sc.uuid, name: sc.name }))
          .sort((a, b) => a.name.localeCompare(b.name, "ru"));
        this.isServiceCentersLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isServiceCentersLoading = false;
      });
    }
  };

  loadSummary = async () => {
    this.isSummaryLoading = true;
    try {
      const response = await superadminGetClientsSummary(
        this.apiSummaryParams(),
      );
      runInAction(() => {
        this.summary = response as unknown as ClientSummaryDto;
        this.isSummaryLoading = false;
      });
      this.saveToCache();
    } catch {
      // Endpoint may not be deployed yet (404) — fail silently
      runInAction(() => {
        this.isSummaryLoading = false;
      });
    }
  };

  loadAll = async () => {
    await Promise.all([
      this.loadClients(),
      this.loadSummary(),
      this.loadServiceCenters(),
    ]);
  };

  // ── Month cache ──

  private saveToCache() {
    const key = monthKey(this.activeMonth);
    const existing = this.monthDataCache.get(key);
    this.monthDataCache.set(key, {
      clients:
        existing?.clients !== this.clients
          ? [...this.clients]
          : existing.clients,
      meta: this.meta,
      summary: this.summary ?? existing?.summary ?? null,
    });
  }

  private restoreFromCache(month: Date): boolean {
    const cached = this.monthDataCache.get(monthKey(month));
    if (!cached) return false;
    this.clients = cached.clients;
    this.meta = cached.meta;
    this.summary = cached.summary;
    this.page = cached.meta?.page ?? 1;
    return true;
  }

  private prefetchAdjacentMonth(direction: 1 | -1) {
    const adj = new Date(this.activeMonth);
    adj.setMonth(adj.getMonth() + direction);
    const key = monthKey(adj);
    if (this.monthDataCache.has(key)) return;

    Promise.all([
      superadminListClients(this.apiListParams(1, adj)),
      superadminGetClientsSummary(this.apiSummaryParams(adj)),
    ])
      .then(([listResp, summaryResp]) => {
        const { items, meta } = parseListResponse(listResp, this.limit);
        runInAction(() => {
          this.monthDataCache.set(key, {
            clients: items,
            meta,
            summary: summaryResp as unknown as ClientSummaryDto,
          });
        });
      })
      .catch(() => {
        /* prefetch failure is non-critical */
      });
  }

  private invalidateCache() {
    this.monthDataCache.clear();
  }

  // ── Setters ──

  /** Only updates the search value — API call is debounced from the component. */
  setSearchValue = (value: string) => {
    this.search = value;
    this.page = 1;
    this.invalidateCache();
  };

  setSearch = (value: string) => {
    this.setSearchValue(value);
    this.loadAll();
  };

  setSortDescriptor = (descriptor: SortDescriptor) => {
    this.sortDescriptor = descriptor;
    this.page = 1;
    this.invalidateCache();
    this.loadClients();
  };

  setFilters = (filters: ClientsFilters) => {
    this.filters = filters;
    this.page = 1;
    this.invalidateCache();
    this.loadAll();
  };

  resetFilters = () => {
    this.filters = emptyFilters();
    this.page = 1;
    this.invalidateCache();
    this.loadAll();
  };

  setActiveMonth = (month: Date) => {
    this.activeMonth = month;
    this.page = 1;
    // Reset filters and sort when navigating between months
    this.filters = emptyFilters();
    this.sortDescriptor = { column: "", direction: "descending" };
    this.restoreFromCache(month);
    this.loadAll();
  };

  goToPrevMonth = () => {
    const n = new Date(this.activeMonth);
    n.setMonth(n.getMonth() - 1);
    this.setActiveMonth(n);
    this.prefetchAdjacentMonth(-1);
  };

  goToNextMonth = () => {
    const n = new Date(this.activeMonth);
    n.setMonth(n.getMonth() + 1);
    this.setActiveMonth(n);
    this.prefetchAdjacentMonth(1);
  };
}

export const clientsStore = new ClientsStore();
export type { ClientsStore };
